import React, { useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useDispatch, useSelector } from 'react-redux';
import { getEditorValue, setEditorValue } from 'store/ai-modal';
import { bindFormulaEvents } from './plugins/formula';
import { openAiAssistant } from './plugins/aiAssistant';
import { bindWidgetEvents } from './widgets';
import { buildJoditConfig } from './jodit.config';
import JoditMenuBar from './JoditMenuBar';
import styles from './widgets/mc-widgets.module.scss';

const JoditEditor = dynamic(() => import('jodit-react'), {
  ssr: false,
});

const JoditEditorShell = ({
  editorState = '',
  height = 600,
  width = '100%',
  setEditorState = () => {},
  placeholder = '',
  disabled = false,
  enableInteractiveWidgets = false,
  courseId,
}) => {
  const editor = useRef(null);
  const dispatch = useDispatch();
  const editorValueFromRedux = useSelector(getEditorValue);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
    return () => {
      setIsClient(false);
    };
  }, []);

  const config = useMemo(
    () =>
      buildJoditConfig({
        disabled,
        height,
        width,
        placeholder,
        dispatch,
        enableInteractiveWidgets,
        courseId,
        onAfterInit: (editorInstance) => {
          editor.current = { editor: editorInstance };
          bindFormulaEvents(editorInstance);
          if (enableInteractiveWidgets) {
            bindWidgetEvents(editorInstance);
          }
        },
        onChange: (newContent) => {
          setEditorState(newContent);
        },
      }),
    [
      disabled,
      height,
      width,
      placeholder,
      dispatch,
      enableInteractiveWidgets,
      courseId,
    ],
  );

  useEffect(() => {
    if (editorValueFromRedux && editor.current?.editor) {
      editor.current.editor.value = editorValueFromRedux;
      setEditorState(editorValueFromRedux);
      dispatch(setEditorValue(null));
    }
  }, [editorValueFromRedux]);

  if (!isClient) {
    return null;
  }

  return (
    <div className={`jodit-classic-editor ${styles.root}`}>
      <JoditMenuBar
        editorRef={editor}
        disabled={disabled}
        enableInteractiveWidgets={enableInteractiveWidgets}
        onAiOpen={(editorInstance) => openAiAssistant(editorInstance, dispatch)}
      />
      <JoditEditor
        editorRef={editor}
        value={editorState}
        config={config}
        onChange={(newContent) => setEditorState(newContent)}
      />
    </div>
  );
};

export default JoditEditorShell;
